import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type {
  LibraryCardPrintCustomField,
  LibraryCardPrintSettings,
} from "@/lib/libraryCardPrintConfig";

export type RegistrationFieldCatalogEntry = {
  key: string;
  label: string;
  hint?: string;
};

const PRINT_TOGGLES = [
  ["showLibraryFullName", "Library full name (from Branding)"],
  ["showAddressOrSubtitle", "Address or extra line under name"],
  ["showStampImage", "Stamp image"],
  ["showSignatureImage", "Signature image"],
  ["showProfilePhoto", "Patron photo"],
  ["showPatronTypeBanner", "Patron type on banner"],
  ["showCardNumber", "Card number"],
  ["showEmail", "Email"],
  ["showPhone", "Phone"],
  ["showRegistrationDate", "Registration date"],
  ["showSessionLine", "Session line"],
  ["showLibrarianSigLine", "Librarian signature caption"],
] as const;

type PrintLayoutSectionProps = {
  settings: LibraryCardPrintSettings;
  catalog: RegistrationFieldCatalogEntry[];
  onChange: (next: LibraryCardPrintSettings) => void;
};

export function PrintLayoutSection({ settings, catalog, onChange }: PrintLayoutSectionProps) {
  const updateField = (i: number, patch: Partial<LibraryCardPrintCustomField>) => {
    const customFormFields = [...settings.customFormFields];
    customFormFields[i] = { ...customFormFields[i], ...patch };
    onChange({ ...settings, customFormFields });
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="inst-sub">Extra line under library name</Label>
          <Input
            id="inst-sub"
            value={settings.institutionSubtitle}
            onChange={(e) => onChange({ ...settings, institutionSubtitle: e.target.value })}
            placeholder="Only if Branding → address is empty"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="adm-prefix">Banner title prefix</Label>
          <Input
            id="adm-prefix"
            value={settings.admissionBannerPrefix}
            onChange={(e) => onChange({ ...settings, admissionBannerPrefix: e.target.value })}
            placeholder="ADMISSION TICKET"
          />
        </div>
      </div>

      <div>
        <p className="mb-3 text-sm font-medium text-slate-900">Visible on printed cards</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {PRINT_TOGGLES.map(([key, label]) => (
            <label
              key={key}
              className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2"
            >
              <Checkbox
                id={`lc-${key}`}
                checked={settings[key]}
                onCheckedChange={(c) => onChange({ ...settings, [key]: c === true })}
              />
              <span className="text-sm text-slate-700">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sess-lab">Session line label</Label>
          <Input
            id="sess-lab"
            value={settings.sessionLineLabel}
            onChange={(e) => onChange({ ...settings, sessionLineLabel: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sig-lab">Librarian line label</Label>
          <Input
            id="sig-lab"
            value={settings.librarianSigLabel}
            onChange={(e) => onChange({ ...settings, librarianSigLabel: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cpr">Cards per row (max 2)</Label>
          <Input
            id="cpr"
            type="number"
            min={1}
            max={2}
            value={settings.cardsPerRow}
            onChange={(e) =>
              onChange({
                ...settings,
                cardsPerRow: Math.min(2, Math.max(1, parseInt(e.target.value, 10) || 2)),
              })
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="rpp">Rows per page (max 4)</Label>
          <Input
            id="rpp"
            type="number"
            min={1}
            max={4}
            value={settings.rowsPerPage}
            onChange={(e) =>
              onChange({
                ...settings,
                rowsPerPage: Math.min(4, Math.max(1, parseInt(e.target.value, 10) || 4)),
              })
            }
          />
        </div>
      </div>

      <Alert className="border-slate-200 bg-slate-50">
        <AlertDescription className="text-sm text-slate-600">
          Each card prints the fields configured for that patron’s type when a value was saved.
          Select and radio answers print the option label. Lines here set the label and order, and
          must match field names from{" "}
          <Link to="/setup/registration" className="font-medium text-primary underline">
            Setup → Registration
          </Link>
          .
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm font-medium">Extra lines from registration</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() =>
              onChange({
                ...settings,
                customFormFields: [...settings.customFormFields, { label: "", formDataKey: "" }],
              })
            }
          >
            <Plus className="h-4 w-4" />
            Add field
          </Button>
        </div>

        {catalog.length === 0 ? (
          <p className="text-xs text-slate-500">
            No custom registration fields loaded. Configure them under Registration setup, then
            refresh.
          </p>
        ) : null}

        {settings.customFormFields.map((row, i) => (
          <div key={i} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-slate-500">Insert from registration</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm"
                value=""
                aria-label="Pick a registration field"
                onChange={(e) => {
                  const key = e.target.value;
                  e.target.value = "";
                  if (!key) return;
                  const found = catalog.find((x) => x.key === key);
                  updateField(i, { formDataKey: key, label: found?.label || key });
                }}
              >
                <option value="">Choose field…</option>
                {catalog.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label} ({opt.key})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[120px] flex-1 flex flex-col gap-1.5">
                <Label className="text-xs text-slate-500">Label on card</Label>
                <Input
                  value={row.label}
                  onChange={(e) => updateField(i, { label: e.target.value })}
                  placeholder="e.g. Programme"
                />
              </div>
              <div className="min-w-[120px] flex-1 flex flex-col gap-1.5">
                <Label className="text-xs text-slate-500">Form data key</Label>
                <Input
                  value={row.formDataKey}
                  onChange={(e) => updateField(i, { formDataKey: e.target.value })}
                  placeholder="e.g. programme"
                  className="font-mono text-xs"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-red-600"
                onClick={() =>
                  onChange({
                    ...settings,
                    customFormFields: settings.customFormFields.filter((_, j) => j !== i),
                  })
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
